import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PageLayout } from '../components/Sidebar';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import WorkflowEditor from '../components/dashboard/WorkflowEditor';
import MemoryV2Panel from '../components/MemoryV2Panel';
import BlockedJobsBanner from '../components/BlockedJobsBanner';
import ModelRoutingPanel from '../components/ModelRoutingPanel';
import RichMessage from '../components/RichMessage';
import { KnowledgeSourcesPanel } from '../components/KnowledgeSourcesPanel';
import { ReasoningPreviewPanel } from '../components/ReasoningPreviewPanel';
import { TaskPreviewTable } from '../components/TaskPreviewTable';
import { EntityPreviewCard } from '../components/EntityPreviewCard';
import { ThinkingPanel } from '../components/ThinkingPanel';
import {
  Send, Bot, User, Sparkles, RefreshCw, MessageSquare, Inbox, 
  Clock, CheckCircle2, AlertCircle, Archive, ChevronRight,
  History, Plus, Loader2, Zap, Shield, Eye, Play, Pause, 
  Calendar, FileText, GitBranch, Settings2, Workflow, Timer,
  Trash2, Edit2, Copy, Upload, Search, Users, Mail, Activity,
  DollarSign, BarChart3, Phone, Globe, MessageCircle, UserCheck,
  Mic, MicOff, Square, BookOpen, Tag, X, Save, PenLine, Wand2, GraduationCap, ChevronDown, Filter, Cpu,
  Info, Edit3, Pencil, Brain, Network,
} from 'lucide-react';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { RecentActionsPanel } from '../components/RecentActionsPanel';
import { CapabilityTryRow } from '../components/CapabilityTryRow';
import { CapabilityOverrideEditor } from '../components/CapabilityOverrideEditor';
import { OrchestratorPromptCard } from '../components/OrchestratorPromptCard';
import { ChatBlocks } from '../components/ChatBlocks';

const API = '/api';

// Format a timestamp into a compact, human-friendly relative string
// ("just now", "5 min ago", "3 h ago", "2 d ago", "23 Apr 2026").
const formatRelativeTime = (date) => {
  if (!date || isNaN(date.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} d ago`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// ==================== CHAT MESSAGE COMPONENT ====================

const ChatMessage = ({ message, isUser }) => {
  // Message timestamp — prefer created_at, fall back to updated_at. Backend may
  // send either ISO string or {$date} Mongo shape; handle both gracefully.
  const rawTs = message?.created_at || message?.updated_at || message?.timestamp;
  const tsDate = rawTs ? new Date(rawTs) : null;
  const tsValid = tsDate && !isNaN(tsDate.getTime());
  const tsTitle = tsValid ? tsDate.toLocaleString() : 'Sent just now';
  const tsShort = tsValid
    ? tsDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div
      className={cn("flex gap-3 p-4 rounded-lg", isUser ? "bg-primary/5 ml-8" : "bg-muted/50 mr-8")}
      title={tsTitle}
      data-testid={`chat-message-${isUser ? 'user' : 'ai'}`}
      data-timestamp={tsValid ? tsDate.toISOString() : ''}
    >
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", isUser ? "bg-primary text-primary-foreground" : "bg-gradient-to-br from-violet-500 to-indigo-600 text-white")}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
          <span>{isUser ? 'You' : 'Orka AI'}</span>
          {tsShort && (
            <span className="text-[10px] opacity-60" title={tsTitle}>· {tsShort}</span>
          )}
        </div>
        {isUser ? (
          <p className="text-sm leading-relaxed">{message.content}</p>
        ) : (
          <>
            {message.scratchpad && message.scratchpad.length > 0 && (
              <div className="mb-2">
                <ThinkingPanel scratchpad={message.scratchpad} />
              </div>
            )}
            <RichMessage content={message.content} className="text-sm" />
            <ChatBlocks message={message} />
          </>
        )}
      </div>
    </div>
  );
};

// ==================== PENDING ACTION CARD (Deck) ====================
// Uses the same rich, editable preview UI we ship inside the chat:
//   - Task-creating / task-updating caps  → <TaskPreviewTable />
//   - Everything else                     → <EntityPreviewCard />
// No raw-JSON dumps — users see clean, themed fields they can reconcile
// against canonical categories, assignees, dates, etc.
const TASK_EDITOR_CAPS = new Set([
  'orka.tasks.create',
  'orka.tasks.bulk_create',
  'orka.tasks.update',
]);

const PendingActionCard = ({ action, onConfirm, onReject }) => {
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const handleReject = async () => {
    setLoading(true);
    try { await onReject(action); setDismissed(true); }
    finally { setLoading(false); }
  };

  const onConfirmed = () => {
    // Action was already committed by the preview's own confirm button —
    // just drop the card and trigger parent refetch.
    setDismissed(true);
    onConfirm?.({ ...action, _already_confirmed: true });
  };

  const riskColors = {
    low: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
    medium: 'bg-amber-500/10 text-amber-600 border-amber-200',
    high: 'bg-red-500/10 text-red-600 border-red-200',
    critical: 'bg-purple-500/10 text-purple-600 border-purple-200'
  };

  const isTaskEditor = TASK_EDITOR_CAPS.has(action.capability_id);

  return (
    <Card className="border-amber-200 bg-amber-50/30 dark:bg-amber-950/10" data-testid={`deck-pending-${action.pending_action_id}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="font-medium truncate">{action.capability_name || action.capability_id}</span>
            {action.risk_tier && (
              <Badge variant="outline" className={cn("text-[10px]", riskColors[action.risk_tier])}>
                {action.risk_tier}
              </Badge>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={handleReject} disabled={loading}
            data-testid={`deck-reject-${action.pending_action_id}`}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Reject'}
          </Button>
        </div>
        {action.description && (
          <p className="text-xs text-muted-foreground mb-2">{action.description}</p>
        )}
        {isTaskEditor ? (
          <TaskPreviewTable pending={action} onConfirmed={onConfirmed} />
        ) : (
          <EntityPreviewCard pending={action} onConfirmed={onConfirmed} />
        )}
      </CardContent>
    </Card>
  );
};

// ==================== CAPABILITY DETAIL ROW (Editable) ====================

const CapabilityDetailRow = ({ capability, onSave, isCustom }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const riskBadge = {
    low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-red-50 text-red-700 border-red-200',
    critical: 'bg-purple-50 text-purple-700 border-purple-200'
  };

  const startEdit = () => {
    setEditData({
      description: capability.description || '',
      knowledge_sources: capability.knowledge_sources || [],
      system_prompt: capability.system_prompt || '',
      risk_tier: capability.risk_tier || 'low',
      confirm_required: capability.confirm_required || false
    });
    setEditing(true);
  };

  const handleSave = () => {
    if (onSave) onSave(capability.capability_id, editData);
    setEditing(false);
  };

  return (
    <div className="group" data-testid={`capability-${capability.capability_id}`}>
      <button className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-muted/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0", isCustom ? "bg-gradient-to-br from-amber-500 to-orange-500" : "bg-gradient-to-br from-violet-500 to-indigo-500")}>
          <Zap className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{capability.name}</span>
            <Badge variant="outline" className={cn("text-[10px]", riskBadge[capability.risk_tier || 'low'])}>{capability.risk_tier || 'low'}</Badge>
            {capability.confirm_required && <Badge variant="secondary" className="text-[10px]">Needs Confirm</Badge>}
            {isCustom && <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">Custom</Badge>}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{capability.description}</p>
        </div>
        <ChevronRight className={cn("w-4 h-4 text-muted-foreground transition-transform", expanded && "rotate-90")} />
      </button>
      {expanded && (
        <div className="px-5 pb-4 ml-13 space-y-3 border-l-2 border-violet-200 ml-[2.8rem]">
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Description</label>
                <Textarea value={editData.description} onChange={e => setEditData(p => ({...p, description: e.target.value}))} className="mt-1 text-sm" rows={2} />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Knowledge Sources (comma-separated)</label>
                <Input value={(editData.knowledge_sources || []).join(', ')} onChange={e => setEditData(p => ({...p, knowledge_sources: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))} className="mt-1 text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">System Prompt</label>
                <Textarea value={editData.system_prompt} onChange={e => setEditData(p => ({...p, system_prompt: e.target.value}))} className="mt-1 text-xs font-mono" rows={4} placeholder="Custom system prompt for this capability..." />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave}><Save className="w-3 h-3 mr-1" />Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">What it does</label>
                <p className="text-sm mt-1">{capability.description}</p>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Capability ID</label>
                <code className="text-xs bg-muted px-2 py-0.5 rounded mt-1 block w-fit">{capability.capability_id}</code>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Knowledge Sources</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(capability.knowledge_sources || ['Orka Platform Data']).map((ks, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">{ks}</Badge>
                  ))}
                </div>
              </div>
              {capability.system_prompt && (
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">System Prompt</label>
                  <pre className="text-xs mt-1 bg-muted/50 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">{capability.system_prompt}</pre>
                </div>
              )}
              {(isCustom || onSave) && (
                <Button size="sm" variant="outline" onClick={startEdit} className="text-xs"><Edit2 className="w-3 h-3 mr-1" />Edit</Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CAPABILITY CARD ====================

const CapabilityCard = ({ capability }) => {
  const riskColors = {
    low: 'border-emerald-200 bg-emerald-50/50',
    medium: 'border-amber-200 bg-amber-50/50',
    high: 'border-red-200 bg-red-50/50',
    critical: 'border-purple-200 bg-purple-50/50'
  };

  return (
    <Card className={cn("orka-transition-colors hover:shadow-md", riskColors[capability.risk_tier])}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-violet-500" />
              <span className="font-medium">{capability.name}</span>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{capability.description}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className="text-xs">{capability.risk_tier}</Badge>
            {capability.confirm_required && <Badge variant="secondary" className="text-xs">Confirm</Badge>}
          </div>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          <code className="bg-black/5 px-1 rounded">{capability.capability_id}</code>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== WORKFLOW CARD ====================

const WorkflowCard = ({ workflow, onEdit, onDelete, onRun }) => (
  <Card className="hover:shadow-md orka-transition-colors">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <GitBranch className="w-4 h-4 text-violet-500" />
            <span className="font-medium">{workflow.name}</span>
            {workflow.is_published && <Badge variant="secondary" className="text-xs">Published</Badge>}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">{workflow.description || 'No description'}</p>
          <div className="text-xs text-muted-foreground mt-2">
            {workflow.blocks?.length || 0} blocks • {workflow.schedule ? 'Scheduled' : 'Manual'}
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onRun(workflow)} title="Run">
            <Play className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(workflow)} title="Edit">
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(workflow)} title="Delete">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// ==================== SCHEDULED JOB CARD ====================

const ScheduledJobCard = ({ job, onToggle, onEdit, onDelete }) => (
  <Card className={cn("hover:shadow-md orka-transition-colors", !job.is_active && "opacity-60")}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Timer className="w-4 h-4 text-violet-500" />
            <span className="font-medium">{job.name}</span>
            <Badge variant={job.is_active ? "default" : "secondary"} className="text-xs">
              {job.is_active ? 'Active' : 'Paused'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">Type: {job.job_type}</p>
          <div className="text-xs text-muted-foreground mt-1">
            {job.schedule?.type === 'daily' && `Daily at ${job.schedule.time}`}
            {job.schedule?.type === 'cron' && `Cron: ${job.schedule.expression}`}
            {job.last_run_at && ` • Last run: ${new Date(job.last_run_at).toLocaleDateString()}`}
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onToggle(job)} title={job.is_active ? 'Pause' : 'Resume'}>
            {job.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(job)} title="Edit">
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(job)} title="Delete">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// ==================== ARTIFACT CARD ====================

const ArtifactCard = ({ artifact, onClick }) => {
  const typeIcons = { briefing: Calendar, report: FileText, summary: Eye, plan: GitBranch };
  const Icon = typeIcons[artifact.type] || FileText;

  return (
    <Card className="hover:shadow-md orka-transition-colors cursor-pointer" onClick={() => onClick(artifact)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Icon className="w-5 h-5 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{artifact.title}</div>
            <div className="text-sm text-muted-foreground capitalize">{artifact.type}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {new Date(artifact.created_at).toLocaleDateString()} • v{artifact.version}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== DECK ITEM ====================

const DeckItem = ({ item, onRead, onArchive }) => {
  const typeIcons = {
    artifact: <Sparkles className="w-4 h-4 text-violet-500" />,
    notification: <MessageSquare className="w-4 h-4 text-blue-500" />,
    action_required: <AlertCircle className="w-4 h-4 text-amber-500" />,
    report: <Eye className="w-4 h-4 text-emerald-500" />
  };

  return (
    <div className={cn("p-4 border-b hover:bg-muted/50 cursor-pointer transition-colors group", !item.is_read && "bg-primary/5")} onClick={() => !item.is_read && onRead(item)}>
      <div className="flex items-start gap-3">
        <div className="mt-1">{typeIcons[item.item_type] || <MessageSquare className="w-4 h-4" />}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("font-medium", !item.is_read && "text-primary")}>{item.title}</span>
            {!item.is_read && <Badge variant="default" className="text-xs">New</Badge>}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">{item.summary}</p>
          <div className="text-xs text-muted-foreground mt-1">{new Date(item.created_at).toLocaleString()}</div>
        </div>
        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); onArchive(item); }}>
          <Archive className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ==================== MAIN AI PAGE ====================

const AIPage = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  
  // State
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [liveRunId, setLiveRunId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [deckItems, setDeckItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [capabilities, setCapabilities] = useState([]);
  const [v2Caps, setV2Caps] = useState({ stats: null, groups: {}, domain_labels: {}, overrides_count: 0 });
  const [v2DomainFilter, setV2DomainFilter] = useState('');  // '' = all
  const [editingCapabilityId, setEditingCapabilityId] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  const [activeSideTab, setActiveSideTab] = useState('history');
  
  // Workflow editor state
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showWorkflowEditor, setShowWorkflowEditor] = useState(false);
  
  // Memory state
  const [documents, setDocuments] = useState([]);
  const [memoryQuery, setMemoryQuery] = useState('');
  const [memoryResults, setMemoryResults] = useState([]);
  const [uploadContent, setUploadContent] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  
  // Multi-Orca state
  const [orcaInbox, setOrcaInbox] = useState([]);
  const [orcaSent, setOrcaSent] = useState([]);
  const [orcaPending, setOrcaPending] = useState([]);
  
  // Observability state
  const [usageSummary, setUsageSummary] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [recentErrors, setRecentErrors] = useState([]);
  const [modelBreakdown, setModelBreakdown] = useState([]);
  
  // Personalization state
  const [personalization, setPersonalization] = useState(null);
  const [newDoRule, setNewDoRule] = useState('');
  const [newDontRule, setNewDontRule] = useState('');
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '' });
  
  // Reply Library state
  const [replyLibrary, setReplyLibrary] = useState([]);
  const [newReply, setNewReply] = useState({ context: '', reply_text: '', tags: '', channel: '' });
  const [editingReply, setEditingReply] = useState(null);
  
  // Voice session state
  const [voiceMode, setVoiceMode] = useState(false);
  const [lastVoiceAt, setLastVoiceAt] = useState(null);
  const [voiceConvId, setVoiceConvId] = useState(null);
  const [voicePendingActions, setVoicePendingActions] = useState([]);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  
  // Custom capabilities state
  const [customCapabilities, setCustomCapabilities] = useState([]);
  const [showCreateCap, setShowCreateCap] = useState(false);
  const [newCapDesc, setNewCapDesc] = useState('');
  const [generatingCap, setGeneratingCap] = useState(false);
  const [generatedCap, setGeneratedCap] = useState(null);
  
  // System functions state
  const [systemFunctions, setSystemFunctions] = useState([]);
  const [editingFunction, setEditingFunction] = useState(null);
  
  // Learning module state
  const [learningTasks, setLearningTasks] = useState([]);
  const [learningInstructions, setLearningInstructions] = useState([]);
  const [learningTotal, setLearningTotal] = useState(0);
  const [learningPage, setLearningPage] = useState(0);
  const [learningFilter, setLearningFilter] = useState('');
  const [activeLearnTask, setActiveLearnTask] = useState(null);
  const [learnDescription, setLearnDescription] = useState('');
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanedResult, setCleanedResult] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Voice input - auto-send mode for voice chat
  const { isRecording, isTranscribing, duration, startRecording, stopRecording, cancelRecording } = useVoiceInput({
    onTranscript: async (text) => {
      // Check for verbal approval of pending actions
      if (voicePendingActions.length > 0) {
        const lower = text.toLowerCase();
        if (lower.includes('yes') || lower.includes('approve') || lower.includes('confirm') || lower.includes('go ahead') || lower.includes('do it')) {
          // Auto-confirm the first pending action
          const action = voicePendingActions[0];
          await confirmAction(action);
          setVoicePendingActions(prev => prev.slice(1));
          return;
        }
        if (lower.includes('no') || lower.includes('reject') || lower.includes('cancel') || lower.includes('don\'t')) {
          const action = voicePendingActions[0];
          await rejectAction(action);
          setVoicePendingActions(prev => prev.slice(1));
          return;
        }
      }
      
      // Auto-send voice message as chat
      if (voiceMode) {
        setVoiceProcessing(true);
        try {
          // Get or create voice session
          const sessionRes = await fetch(`${API}/ai/voice/session`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ last_voice_at: lastVoiceAt })
          });
          if (sessionRes.ok) {
            const sessionData = await sessionRes.json();
            const convId = sessionData.conversation_id;
            if (sessionData.is_new) { setMessages([]); }
            setVoiceConvId(convId);
            setConversationId(convId);
            
            // Send the message
            const tempMsg = { message_id: `temp_${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString(), metadata: { source: 'voice' } };
            setMessages(prev => [...prev, tempMsg]);
            
            const chatRes = await fetch(`${API}/ai/voice/chat`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
              body: JSON.stringify({ message: text, conversation_id: convId })
            });
            
            if (chatRes.ok) {
              const data = await chatRes.json();
              setConversationId(data.conversation_id);
              const assistantMsg = { message_id: `resp_${Date.now()}`, role: 'assistant', content: data.response, created_at: new Date().toISOString() };
              setMessages(prev => [...prev, assistantMsg]);
              
              if (data.pending_actions?.length > 0) {
                setVoicePendingActions(data.pending_actions);
                fetchPendingActions();
                toast.info('Action needs your approval - say "yes" or "no"');
              }
              fetchConversations();
            } else {
              const err = await chatRes.json().catch(() => ({ detail: 'Unknown error' }));
              const errMsg = { message_id: `err_${Date.now()}`, role: 'assistant', content: `Voice command error: *${err.detail}*\n\nTask sent to Deck for manual resolution.`, created_at: new Date().toISOString() };
              setMessages(prev => [...prev, errMsg]);
              fetchDeckItems();
            }
          }
        } catch (err) {
          toast.error('Voice processing failed');
        } finally {
          setVoiceProcessing(false);
          setLastVoiceAt(new Date().toISOString());
        }
      } else {
        // Non-voice mode: just set the input
        setInputValue(prev => prev ? prev + ' ' + text : text);
        inputRef.current?.focus();
      }
    }
  });

  const scrollToBottom = useCallback(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, []);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Fetch AI status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API}/ai/status`, { credentials: 'include' });
        if (res.ok) { const data = await res.json(); setAiEnabled(data.enabled); }
      } catch (err) { console.error('Error fetching AI status:', err); }
    };
    fetchStatus();
  }, []);

  // Fetch capabilities
  useEffect(() => {
    const fetchCapabilities = async () => {
      try {
        const res = await fetch(`${API}/ai/capabilities`, { credentials: 'include' });
        if (res.ok) { const data = await res.json(); setCapabilities(data); }
      } catch (err) { console.error('Error fetching capabilities:', err); }
    };
    const fetchV2Caps = async () => {
      try {
        const res = await fetch(`${API}/ai/v2/capabilities?group_by=domain`, { credentials: 'include' });
        if (res.ok) { const data = await res.json(); setV2Caps(data); }
      } catch (err) { console.error('Error fetching v2 capabilities:', err); }
    };
    fetchCapabilities();
    fetchV2Caps();
  }, []);

  // Phase 12 — reusable refresher after override save/reset.
  const refetchV2Caps = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/v2/capabilities?group_by=domain`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setV2Caps(data); }
    } catch (err) { console.error('Error refetching v2 capabilities:', err); }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/conversations`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setConversations(data); }
    } catch (err) { console.error('Error fetching conversations:', err); }
  }, []);
  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Fetch pending actions
  const fetchPendingActions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/pending-actions`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setPendingActions(data); }
    } catch (err) { console.error('Error fetching pending actions:', err); }
  }, []);
  useEffect(() => { fetchPendingActions(); const interval = setInterval(fetchPendingActions, 10000); return () => clearInterval(interval); }, [fetchPendingActions]);

  // Fetch deck items
  const fetchDeckItems = useCallback(async () => {
    try {
      const [itemsRes, countRes] = await Promise.all([
        fetch(`${API}/ai/deck`, { credentials: 'include' }),
        fetch(`${API}/ai/deck/unread-count`, { credentials: 'include' })
      ]);
      if (itemsRes.ok) { const data = await itemsRes.json(); setDeckItems(data); }
      if (countRes.ok) { const data = await countRes.json(); setUnreadCount(data.unread_count); }
    } catch (err) { console.error('Error fetching deck items:', err); }
  }, []);
  useEffect(() => { fetchDeckItems(); }, [fetchDeckItems]);

  // Fetch workflows
  const fetchWorkflows = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/workflows`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setWorkflows(data); }
    } catch (err) { console.error('Error fetching workflows:', err); }
  }, []);
  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  // Fetch scheduled jobs
  const fetchScheduledJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/scheduled-jobs`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setScheduledJobs(data); }
    } catch (err) { console.error('Error fetching scheduled jobs:', err); }
  }, []);
  useEffect(() => { fetchScheduledJobs(); }, [fetchScheduledJobs]);

  // Fetch artifacts
  const fetchArtifacts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/artifacts`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setArtifacts(data); }
    } catch (err) { console.error('Error fetching artifacts:', err); }
  }, []);
  useEffect(() => { fetchArtifacts(); }, [fetchArtifacts]);

  // Fetch documents (Memory)
  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/memory/documents`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setDocuments(data); }
    } catch (err) { console.error('Error fetching documents:', err); }
  }, []);
  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Fetch Multi-Orca data
  const fetchOrcaData = useCallback(async () => {
    try {
      const [inboxRes, sentRes, pendingRes] = await Promise.all([
        fetch(`${API}/ai/orca/inbox`, { credentials: 'include' }),
        fetch(`${API}/ai/orca/sent`, { credentials: 'include' }),
        fetch(`${API}/ai/orca/pending-approvals`, { credentials: 'include' })
      ]);
      if (inboxRes.ok) setOrcaInbox(await inboxRes.json());
      if (sentRes.ok) setOrcaSent(await sentRes.json());
      if (pendingRes.ok) setOrcaPending(await pendingRes.json());
    } catch (err) { console.error('Error fetching Orca data:', err); }
  }, []);
  useEffect(() => { fetchOrcaData(); }, [fetchOrcaData]);

  // Fetch Observability data
  const fetchObservability = useCallback(async () => {
    try {
      const [usageRes, dailyRes, errorsRes, modelsRes] = await Promise.all([
        fetch(`${API}/ai/observability/usage?days=7`, { credentials: 'include' }),
        fetch(`${API}/ai/observability/daily?days=30`, { credentials: 'include' }),
        fetch(`${API}/ai/observability/errors?limit=10`, { credentials: 'include' }),
        fetch(`${API}/ai/observability/models?days=7`, { credentials: 'include' })
      ]);
      if (usageRes.ok) setUsageSummary(await usageRes.json());
      if (dailyRes.ok) setDailyUsage(await dailyRes.json());
      if (errorsRes.ok) setRecentErrors(await errorsRes.json());
      if (modelsRes.ok) setModelBreakdown(await modelsRes.json());
    } catch (err) { console.error('Error fetching observability:', err); }
  }, []);
  useEffect(() => { if (activeTab === 'observability') fetchObservability(); }, [activeTab, fetchObservability]);

  // Fetch Personalization
  const fetchPersonalization = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/personalization`, { credentials: 'include' });
      if (res.ok) setPersonalization(await res.json());
    } catch (err) { console.error('Error fetching personalization:', err); }
  }, []);
  useEffect(() => { if (activeTab === 'personalization') fetchPersonalization(); }, [activeTab, fetchPersonalization]);

  // Fetch Contacts
  const fetchContacts = useCallback(async () => {
    try {
      const [contactsRes, connectorsRes] = await Promise.all([
        fetch(`${API}/ai/contacts`, { credentials: 'include' }),
        fetch(`${API}/ai/connectors`, { credentials: 'include' })
      ]);
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (connectorsRes.ok) setConnectors(await connectorsRes.json());
    } catch (err) { console.error('Error fetching contacts:', err); }
  }, []);
  useEffect(() => { if (activeTab === 'contacts') fetchContacts(); }, [activeTab, fetchContacts]);

  // Reply Library
  const fetchReplyLibrary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/reply-library`, { credentials: 'include' });
      if (res.ok) setReplyLibrary(await res.json());
    } catch (err) { console.error('Error fetching reply library:', err); }
  }, []);
  useEffect(() => { if (activeTab === 'reply-library') fetchReplyLibrary(); }, [activeTab, fetchReplyLibrary]);

  // Custom Capabilities
  const fetchCustomCapabilities = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/capabilities/custom`, { credentials: 'include' });
      if (res.ok) setCustomCapabilities(await res.json());
    } catch (err) { console.error('Error fetching custom caps:', err); }
  }, []);
  useEffect(() => { if (activeTab === 'capabilities') fetchCustomCapabilities(); }, [activeTab, fetchCustomCapabilities]);
  
  // System Functions
  const fetchSystemFunctions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/system-functions`, { credentials: 'include' });
      if (res.ok) setSystemFunctions(await res.json());
    } catch (err) { console.error('Error fetching system functions:', err); }
  }, []);
  useEffect(() => { if (activeTab === 'capabilities') fetchSystemFunctions(); }, [activeTab, fetchSystemFunctions]);
  
  // Learning
  const fetchLearningTasks = useCallback(async (page = 0) => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '10' });
      if (learningFilter) params.set('search', learningFilter);
      const res = await fetch(`${API}/ai/learning/completed-tasks?${params}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setLearningTasks(d.tasks); setLearningTotal(d.total); setLearningPage(d.page); }
    } catch (err) { console.error('Error fetching learning tasks:', err); }
  }, [learningFilter]);
  
  const fetchLearningInstructions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/learning/instructions`, { credentials: 'include' });
      if (res.ok) setLearningInstructions(await res.json());
    } catch (err) { console.error('Error fetching learning instructions:', err); }
  }, []);
  
  useEffect(() => { if (activeTab === 'capabilities') { fetchLearningTasks(); fetchLearningInstructions(); } }, [activeTab, fetchLearningTasks, fetchLearningInstructions]);

  // Handle URL params (e.g., ?tab=reply-library from Switchboard link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
  }, []);

  // Load conversation
  const loadConversation = useCallback(async (convId) => {
    try {
      const res = await fetch(`${API}/ai/conversations/${convId}/messages`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.filter(m => m.role !== 'system' && m.role !== 'tool'));
        setConversationId(convId);
      }
    } catch (err) { console.error('Error loading conversation:', err); }
  }, []);

  // Send message - with stuck-task recovery
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    // Phase 14.1 — generate a per-send live progress id so the ThinkingPanel
    // can poll real tool calls in real time.
    const liveRunId = `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    setLiveRunId(liveRunId);

    const tempUserMsg = { message_id: `temp_${Date.now()}`, role: 'user', content: userMessage, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage, conversation_id: conversationId, surface: 'ai_page', live_run_id: liveRunId })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversationId(data.conversation_id);
        const assistantMsg = {
          message_id: `resp_${Date.now()}`,
          role: 'assistant',
          content: data.response,
          created_at: new Date().toISOString(),
          tool_failures: data.tool_failures || [],
          pending_actions: data.pending_actions || [],
          scratchpad: data.scratchpad || [],
        };
        setMessages(prev => [...prev, assistantMsg]);
        if (data.pending_actions?.length > 0) { fetchPendingActions(); }
        fetchConversations();
      } else {
        const error = await res.json().catch(() => ({ detail: 'Unknown error' }));
        // AI failed - add error message and offer recovery
        const errorMsg = {
          message_id: `err_${Date.now()}`,
          role: 'assistant',
          content: `I ran into an issue processing that request: *${error.detail || 'Unknown error'}*\n\nYou can continue chatting — I'll try again. If this keeps happening, try starting a new conversation.`,
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMsg]);
        // Send stuck task to deck for manual resolution
        try {
          await fetch(`${API}/ai/deck/add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              title: `Failed AI task: ${userMessage.slice(0, 80)}`,
              summary: `AI could not complete: "${userMessage}". Error: ${error.detail || 'Unknown'}. Sent to deck for manual resolution.`,
              item_type: 'action_required'
            })
          });
          fetchDeckItems();
        } catch (deckErr) { /* deck add failed silently */ }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMsg = {
        message_id: `err_${Date.now()}`,
        role: 'assistant',
        content: 'I couldn\'t process that request due to a connection issue. You can keep chatting or start a new conversation.',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
      toast.error('Failed to send message');
    }
    finally { 
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  // Start new conversation
  const startNewConversation = async () => {
    try {
      const res = await fetch(`${API}/ai/conversations/new`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConversationId(data.conversation_id);
        setMessages([]);
        fetchConversations();
        toast.success('New conversation started');
      }
    } catch (err) { toast.error('Failed to start new conversation'); }
  };

  // Confirm/Reject actions
  const confirmAction = async (action) => {
    // If the rich preview already committed the action, just refresh the deck.
    if (action?._already_confirmed) {
      fetchPendingActions();
      return;
    }
    try {
      const res = await fetch(`${API}/ai/pending-actions/${action.pending_action_id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation_token: action.confirmation_token })
      });
      if (res.ok) { toast.success('Action confirmed and executed'); fetchPendingActions(); }
      else { const error = await res.json(); toast.error('Failed', { description: error.detail }); }
    } catch (err) { toast.error('Failed to confirm action'); }
  };

  const rejectAction = async (action) => {
    try {
      const res = await fetch(`${API}/ai/pending-actions/${action.pending_action_id}/reject`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Action rejected'); fetchPendingActions(); }
    } catch (err) { toast.error('Failed to reject action'); }
  };

  // Deck operations
  const markDeckItemRead = async (item) => {
    try { await fetch(`${API}/ai/deck/${item.deck_item_id}/read`, { method: 'POST', credentials: 'include' }); fetchDeckItems(); }
    catch (err) { console.error('Error marking item read:', err); }
  };

  const archiveDeckItem = async (item) => {
    try { await fetch(`${API}/ai/deck/${item.deck_item_id}/archive`, { method: 'POST', credentials: 'include' }); fetchDeckItems(); toast.success('Item archived'); }
    catch (err) { toast.error('Failed to archive item'); }
  };

  // Workflow operations
  const createWorkflow = async () => {
    // Open editor with new workflow
    setEditingWorkflow(null);
    setShowWorkflowEditor(true);
  };

  const editWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setShowWorkflowEditor(true);
  };

  const saveWorkflow = async (workflowData) => {
    try {
      if (editingWorkflow) {
        // Update existing
        await fetch(`${API}/ai/workflows/${editingWorkflow.workflow_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(workflowData)
        });
      } else {
        // Create new
        await fetch(`${API}/ai/workflows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(workflowData)
        });
      }
      fetchWorkflows();
      setShowWorkflowEditor(false);
      setEditingWorkflow(null);
    } catch (err) {
      throw err;
    }
  };

  const deleteWorkflow = async (workflow) => {
    if (!window.confirm(`Delete workflow "${workflow.name}"?`)) return;
    try {
      await fetch(`${API}/ai/workflows/${workflow.workflow_id}`, { method: 'DELETE', credentials: 'include' });
      fetchWorkflows();
      toast.success('Workflow deleted');
    } catch (err) { toast.error('Failed to delete workflow'); }
  };

  const runWorkflow = async (workflow) => {
    toast.info('Workflow execution coming soon - workflows run on schedule or via API');
  };

  // Memory operations
  const searchMemory = async () => {
    if (!memoryQuery.trim()) return;
    try {
      const res = await fetch(`${API}/ai/memory/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: memoryQuery, limit: 10 })
      });
      if (res.ok) {
        const data = await res.json();
        setMemoryResults(data);
      }
    } catch (err) { toast.error('Search failed'); }
  };

  const uploadDocument = async () => {
    if (!uploadContent.trim() || !uploadTitle.trim()) {
      toast.error('Title and content required');
      return;
    }
    try {
      const res = await fetch(`${API}/ai/memory/store`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: uploadTitle, content: uploadContent, memory_type: 'document' })
      });
      if (res.ok) {
        toast.success('Document stored');
        setUploadTitle('');
        setUploadContent('');
        fetchDocuments();
      }
    } catch (err) { toast.error('Upload failed'); }
  };

  const deleteDocument = async (docId) => {
    try {
      await fetch(`${API}/ai/memory/documents/${docId}`, { method: 'DELETE', credentials: 'include' });
      fetchDocuments();
      toast.success('Document deleted');
    } catch (err) { toast.error('Delete failed'); }
  };

  // Multi-Orca operations
  const approveOrcaMessage = async (messageId) => {
    try {
      await fetch(`${API}/ai/orca/messages/${messageId}/approve`, { method: 'POST', credentials: 'include' });
      fetchOrcaData();
      toast.success('Message approved');
    } catch (err) { toast.error('Approval failed'); }
  };

  const rejectOrcaMessage = async (messageId) => {
    try {
      await fetch(`${API}/ai/orca/messages/${messageId}/reject`, { method: 'POST', credentials: 'include' });
      fetchOrcaData();
      toast.success('Message rejected');
    } catch (err) { toast.error('Rejection failed'); }
  };

  // Personalization operations
  const updatePersonalization = async (updates) => {
    try {
      const res = await fetch(`${API}/ai/personalization`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const data = await res.json();
        setPersonalization(data);
        toast.success('Preferences saved');
      }
    } catch (err) { toast.error('Failed to save preferences'); }
  };

  const addRule = async (ruleType, rule) => {
    if (!rule.trim()) return;
    try {
      await fetch(`${API}/ai/personalization/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type: ruleType, rule })
      });
      fetchPersonalization();
      if (ruleType === 'do') setNewDoRule('');
      else setNewDontRule('');
      toast.success('Rule added');
    } catch (err) { toast.error('Failed to add rule'); }
  };

  const removeRule = async (ruleType, rule) => {
    try {
      await fetch(`${API}/ai/personalization/rules`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type: ruleType, rule })
      });
      fetchPersonalization();
      toast.success('Rule removed');
    } catch (err) { toast.error('Failed to remove rule'); }
  };

  // Contact operations
  const addContact = async () => {
    if (!newContact.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const res = await fetch(`${API}/ai/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newContact)
      });
      if (res.ok) {
        fetchContacts();
        setNewContact({ name: '', email: '', phone: '' });
        toast.success('Contact added');
      }
    } catch (err) { toast.error('Failed to add contact'); }
  };

  const toggleOrkaAccess = async (contactId, enabled) => {
    try {
      await fetch(`${API}/ai/contacts/${contactId}/enable-orka`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled })
      });
      fetchContacts();
      toast.success(enabled ? 'Orka access enabled' : 'Orka access disabled');
    } catch (err) { toast.error('Failed to update contact'); }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await fetch(`${API}/ai/contacts/${contactId}`, { method: 'DELETE', credentials: 'include' });
      fetchContacts();
      toast.success('Contact deleted');
    } catch (err) { toast.error('Failed to delete contact'); }
  };

  const runJobNow = async (jobId) => {
    try {
      const res = await fetch(`${API}/ai/scheduled-jobs/${jobId}/run`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success('Job executed');
        fetchScheduledJobs();
        fetchDeckItems();
        fetchArtifacts();
      }
    } catch (err) { toast.error('Failed to run job'); }
  };

  // Scheduled job operations
  const createScheduledJob = async () => {
    const name = prompt('Job name:');
    if (!name) return;
    try {
      const res = await fetch(`${API}/ai/scheduled-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, job_type: 'briefing', schedule: { type: 'daily', time: '08:00' } })
      });
      if (res.ok) { fetchScheduledJobs(); toast.success('Job created'); }
    } catch (err) { toast.error('Failed to create job'); }
  };

  const toggleJob = async (job) => {
    try {
      await fetch(`${API}/ai/scheduled-jobs/${job.job_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !job.is_active })
      });
      fetchScheduledJobs();
    } catch (err) { toast.error('Failed to update job'); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete job "${job.name}"?`)) return;
    try {
      await fetch(`${API}/ai/scheduled-jobs/${job.job_id}`, { method: 'DELETE', credentials: 'include' });
      fetchScheduledJobs();
      toast.success('Job deleted');
    } catch (err) { toast.error('Failed to delete job'); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  // Custom Capability CRUD
  const saveCustomCapability = async (capId, updates) => {
    try {
      const res = await fetch(`${API}/ai/capabilities/custom/${capId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (res.ok) { fetchCustomCapabilities(); toast.success('Capability updated'); }
    } catch { toast.error('Failed to update'); }
  };

  const generateCapability = async () => {
    if (!newCapDesc.trim()) { toast.error('Describe what the capability should do'); return; }
    setGeneratingCap(true);
    try {
      const res = await fetch(`${API}/ai/capabilities/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ description: newCapDesc })
      });
      if (res.ok) { setGeneratedCap(await res.json()); }
      else { toast.error('Generation failed'); }
    } catch { toast.error('Failed to generate'); }
    finally { setGeneratingCap(false); }
  };

  const deployCapability = async () => {
    if (!generatedCap) return;
    try {
      const res = await fetch(`${API}/ai/capabilities/custom`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(generatedCap)
      });
      if (res.ok) {
        fetchCustomCapabilities();
        setGeneratedCap(null); setNewCapDesc(''); setShowCreateCap(false);
        toast.success('Capability deployed');
      }
    } catch { toast.error('Failed to deploy'); }
  };

  const deleteCustomCapability = async (capId) => {
    if (!window.confirm('Delete this capability?')) return;
    try {
      await fetch(`${API}/ai/capabilities/custom/${capId}`, { method: 'DELETE', credentials: 'include' });
      fetchCustomCapabilities(); toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  // System Functions
  const updateSystemFunction = async (fnId, updates) => {
    try {
      await fetch(`${API}/ai/system-functions/${fnId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updates)
      });
      fetchSystemFunctions(); setEditingFunction(null); toast.success('Function updated');
    } catch { toast.error('Failed to update'); }
  };

  // Learning
  const startLearnTask = (task) => {
    setActiveLearnTask(task);
    setLearnDescription('');
    setCleanedResult(null);
  };

  const cleanUpDescription = async () => {
    if (!learnDescription.trim() || !activeLearnTask) return;
    setCleaningUp(true);
    try {
      const res = await fetch(`${API}/ai/learning/cleanup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ raw_description: learnDescription, task_title: activeLearnTask.title })
      });
      if (res.ok) { setCleanedResult(await res.json()); }
      else { toast.error('Cleanup failed'); }
    } catch { toast.error('Failed'); }
    finally { setCleaningUp(false); }
  };

  const saveLearning = async () => {
    if (!cleanedResult || !activeLearnTask) return;
    try {
      await fetch(`${API}/ai/learning/instructions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          task_id: activeLearnTask.task_id,
          task_title: activeLearnTask.title,
          raw_description: learnDescription,
          cleaned_instructions: cleanedResult.cleaned_instructions,
          steps: cleanedResult.steps || [],
          tags: cleanedResult.tags || [],
          work_area_id: activeLearnTask.work_area_id || '',
          source: 'typed'
        })
      });
      setActiveLearnTask(null); setCleanedResult(null); setLearnDescription('');
      fetchLearningTasks(); fetchLearningInstructions();
      toast.success('Instruction saved to Learning Library');
    } catch { toast.error('Failed to save'); }
  };

  const deleteLearning = async (id) => {
    if (!window.confirm('Delete this instruction?')) return;
    try {
      await fetch(`${API}/ai/learning/instructions/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchLearningInstructions(); toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  // Voice Learning
  const { isRecording: isLearnRecording, isTranscribing: isLearnTranscribing, duration: learnDuration, startRecording: startLearnRecording, stopRecording: stopLearnRecording, cancelRecording: cancelLearnRecording } = useVoiceInput({
    onTranscript: (text) => { setLearnDescription(prev => prev ? prev + ' ' + text : text); }
  });

  // Reply Library CRUD
  const addReply = async () => {
    if (!newReply.context.trim() || !newReply.reply_text.trim()) { toast.error('Context and reply text required'); return; }
    try {
      const res = await fetch(`${API}/ai/reply-library`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newReply, tags: newReply.tags ? newReply.tags.split(',').map(t => t.trim()) : [] })
      });
      if (res.ok) { fetchReplyLibrary(); setNewReply({ context: '', reply_text: '', tags: '', channel: '' }); toast.success('Reply added'); }
    } catch { toast.error('Failed to add reply'); }
  };
  
  const updateReply = async (reply) => {
    try {
      await fetch(`${API}/ai/reply-library/${reply.reply_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ context: reply.context, reply_text: reply.reply_text, tags: reply.tags, channel: reply.channel })
      });
      fetchReplyLibrary(); setEditingReply(null); toast.success('Updated');
    } catch { toast.error('Failed to update'); }
  };
  
  const deleteReply = async (replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await fetch(`${API}/ai/reply-library/${replyId}`, { method: 'DELETE', credentials: 'include' });
      fetchReplyLibrary(); toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (!aiEnabled) {
    return (
      <PageLayout title="Orka AI" user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
        <Card className="max-w-lg mx-auto mt-20">
          <CardContent className="pt-6 text-center">
            <Bot className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">AI Service Unavailable</h2>
            <p className="text-muted-foreground">The Orka AI service is currently not configured.</p>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Orka AI" user={user} onLogout={logout} theme={theme} setTheme={setTheme}
      actions={unreadCount > 0 && <Badge variant="secondary" className="bg-violet-100 text-violet-700"><Inbox className="w-3 h-3 mr-1" />{unreadCount} unread</Badge>}
    >
      <div className="h-[calc(100vh-4rem)] flex gap-4 overflow-hidden" data-testid="ai-page">
        {/* Left Sidebar */}
        <div className="w-64 flex-shrink-0 flex flex-col gap-3">
          <Button onClick={startNewConversation} className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700" data-testid="new-chat-btn">
            <Plus className="w-4 h-4 mr-2" />New Chat
          </Button>
          
          <Card className="flex-1 overflow-hidden">
            <Tabs value={activeSideTab} onValueChange={setActiveSideTab} className="h-full flex flex-col">
              <TabsList className="w-full grid grid-cols-3 mx-2 mt-2" style={{ width: 'calc(100% - 16px)' }}>
                <TabsTrigger value="history" className="text-xs"><History className="w-3 h-3 mr-1" />History</TabsTrigger>
                <TabsTrigger value="deck" className="text-xs"><Inbox className="w-3 h-3 mr-1" />Deck{unreadCount > 0 && <span className="ml-1 text-[10px] bg-violet-500 text-white rounded-full w-4 h-4 inline-flex items-center justify-center">{unreadCount}</span>}</TabsTrigger>
                <TabsTrigger value="artifacts" className="text-xs"><FileText className="w-3 h-3 mr-1" />Docs</TabsTrigger>
              </TabsList>
              
              <TabsContent value="history" className="flex-1 overflow-hidden m-0 p-2">
                <ScrollArea className="h-full">
                  {conversations.slice(0, 15).map((conv) => {
                    // Prefer updated_at, fall back to task_updated_at, title_auto_renamed_at, created_at.
                    const rawTs =
                      conv.updated_at ||
                      conv.task_updated_at ||
                      conv.title_auto_renamed_at ||
                      conv.created_at;
                    const tsDate = rawTs ? new Date(rawTs) : null;
                    const tsValid = tsDate && !isNaN(tsDate.getTime());
                    const tsTitle = tsValid ? tsDate.toLocaleString() : 'Date unknown';
                    const tsRelative = tsValid ? formatRelativeTime(tsDate) : '—';
                    return (
                      <button
                        key={conv.conversation_id}
                        onClick={() => loadConversation(conv.conversation_id)}
                        className={cn("w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors", conv.conversation_id === conversationId && "bg-muted")}
                        title={tsTitle}
                        data-testid={`chat-history-item-${conv.conversation_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{conv.title || `Chat ${conv.conversation_id.slice(-6)}`}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{tsRelative}</div>
                      </button>
                    );
                  })}
                  {conversations.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No conversations yet</p>}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="deck" className="flex-1 overflow-hidden m-0 p-2">
                <ScrollArea className="h-full">
                  {deckItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No items in deck</p>
                  ) : (
                    <div className="space-y-1">
                      {deckItems.map((item) => (
                        <div key={item.deck_item_id} className={cn("px-2 py-2 rounded-lg text-sm hover:bg-muted transition-colors cursor-pointer", !item.is_read && "bg-primary/5")} onClick={() => !item.is_read && markDeckItemRead(item)}>
                          <div className="flex items-center gap-1.5">
                            {!item.is_read && <span className="w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />}
                            <span className="font-medium text-xs truncate">{item.title}</span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{item.summary}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</span>
                            <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); archiveDeckItem(item); }}>
                              <Archive className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="artifacts" className="flex-1 overflow-hidden m-0 p-2">
                <ScrollArea className="h-full">
                  <div className="space-y-2">
                    {artifacts.map((art) => (
                      <div key={art.artifact_id} className="px-2 py-2 rounded-lg text-sm hover:bg-muted transition-colors cursor-pointer">
                        <div className="font-medium text-xs truncate">{art.title}</div>
                        <div className="text-xs text-muted-foreground capitalize">{art.type}</div>
                      </div>
                    ))}
                    {artifacts.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No artifacts yet</p>}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <TabsList className="w-fit mb-3 flex-wrap">
              <TabsTrigger value="chat" className="gap-1.5 text-xs"><MessageSquare className="w-3.5 h-3.5" />Chat</TabsTrigger>
              <TabsTrigger value="capabilities" className="gap-1.5 text-xs"><Zap className="w-3.5 h-3.5" />Capabilities</TabsTrigger>
              <TabsTrigger value="knowledge" className="gap-1.5 text-xs" data-testid="ai-page-knowledge-tab"><Network className="w-3.5 h-3.5" />Knowledge</TabsTrigger>
              <TabsTrigger value="reasoning" className="gap-1.5 text-xs" data-testid="ai-page-reasoning-tab"><Brain className="w-3.5 h-3.5" />Reasoning</TabsTrigger>
              <TabsTrigger value="actions" className="gap-1.5 text-xs" data-testid="ai-page-actions-tab"><History className="w-3.5 h-3.5" />Actions</TabsTrigger>
              <TabsTrigger value="workflows" className="gap-1.5 text-xs"><GitBranch className="w-3.5 h-3.5" />Workflows</TabsTrigger>
              <TabsTrigger value="jobs" className="gap-1.5 text-xs"><Timer className="w-3.5 h-3.5" />Jobs</TabsTrigger>
              <TabsTrigger value="memory" className="gap-1.5 text-xs"><Search className="w-3.5 h-3.5" />Memory</TabsTrigger>
              <TabsTrigger value="routing" className="gap-1.5 text-xs" data-testid="ai-page-routing-tab"><Cpu className="w-3.5 h-3.5" />Routing</TabsTrigger>
              <TabsTrigger value="personalization" className="gap-1.5 text-xs"><Settings2 className="w-3.5 h-3.5" />Settings</TabsTrigger>
              <TabsTrigger value="observability" className="gap-1.5 text-xs"><Activity className="w-3.5 h-3.5" />Usage</TabsTrigger>
              <TabsTrigger value="orca" className="gap-1.5 text-xs"><Users className="w-3.5 h-3.5" />Multi-Orca{orcaPending.length > 0 && <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700 text-[10px] px-1">{orcaPending.length}</Badge>}</TabsTrigger>
              <TabsTrigger value="reply-library" className="gap-1.5 text-xs"><BookOpen className="w-3.5 h-3.5" />Reply Library</TabsTrigger>
            </TabsList>
            
            {/* Chat Tab */}
            <TabsContent value="chat" className="flex-1 flex flex-col mt-0 min-h-0 data-[state=inactive]:hidden">
              {pendingActions.length > 0 && (
                <div className="mb-3 space-y-2 flex-shrink-0">
                  {pendingActions.map((action) => <PendingActionCard key={action.pending_action_id} action={action} onConfirm={confirmAction} onReject={rejectAction} />)}
                </div>
              )}
              
              <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4" data-testid="chat-messages">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mb-4">
                        <Bot className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-semibold mb-2">Hello! I'm Orka AI</h2>
                      <p className="text-muted-foreground max-w-md mb-6 text-sm">Your intelligent operational orchestrator. I can manage tasks, analyze schedules, and automate workflows.</p>
                      <div className="grid grid-cols-2 gap-2 max-w-lg">
                        {["What tasks do I have today?", "Show me my backlog", "Create a task for tomorrow", "Give me a productivity summary"].map((suggestion, i) => (
                          <Button key={i} variant="outline" className="text-xs h-auto py-2.5 px-3 text-left justify-start" onClick={() => { setInputValue(suggestion); inputRef.current?.focus(); }}>
                            <Sparkles className="w-3 h-3 mr-1.5 text-violet-500 flex-shrink-0" />{suggestion}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg) => <ChatMessage key={msg.message_id} message={msg} isUser={msg.role === 'user'} />)}
                      {isLoading && (
                        <div className="flex gap-3 p-3 rounded-lg bg-muted/30 mr-8" data-testid="chat-thinking-live">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-muted-foreground mb-1">Orka AI</div>
                            <ThinkingPanel isLive defaultOpen liveRunId={liveRunId} />
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
                
                <div className="p-3 border-t flex-shrink-0">
                  {/* Voice pending actions - approval cards */}
                  {voicePendingActions.length > 0 && voiceMode && (
                    <div className="mb-2 space-y-1.5">
                      {voicePendingActions.map((action, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 text-sm">
                          <Shield className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span className="flex-1 text-xs">{action.description || action.capability_name}</span>
                          <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => { rejectAction(action); setVoicePendingActions(p => p.filter((_, j) => j !== i)); }}>Reject</Button>
                          <Button size="sm" className="h-6 text-xs" onClick={() => { confirmAction(action); setVoicePendingActions(p => p.filter((_, j) => j !== i)); }}>Approve</Button>
                        </div>
                      ))}
                      <p className="text-[10px] text-amber-600 text-center">Say "yes" to approve or "no" to reject</p>
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    {/* Voice mode toggle */}
                    <Button
                      variant={voiceMode ? "default" : "ghost"}
                      size="icon"
                      className={cn("flex-shrink-0", voiceMode && "bg-violet-600 hover:bg-violet-700 text-white")}
                      onClick={() => setVoiceMode(!voiceMode)}
                      title={voiceMode ? "Voice mode ON - transcriptions auto-send" : "Voice mode OFF - transcriptions go to text input"}
                      data-testid="voice-mode-toggle"
                    >
                      <Mic className="w-4 h-4" />
                    </Button>
                    
                    <Input ref={inputRef} value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder={isRecording ? "Listening..." : voiceProcessing ? "Processing voice..." : isTranscribing ? "Transcribing..." : voiceMode ? "Voice mode ON - click mic to speak" : "Ask Orka AI anything..."} className="flex-1 h-9 text-sm" disabled={isRecording || isTranscribing || voiceProcessing} data-testid="chat-input" />
                    
                    {/* Voice input button */}
                    {isRecording ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-red-500 font-mono tabular-nums animate-pulse min-w-[32px]">{duration}s</span>
                        <Button onClick={stopRecording} size="icon" className="bg-red-500 hover:bg-red-600 animate-pulse" data-testid="stop-recording-btn">
                          <Square className="w-4 h-4 fill-current" />
                        </Button>
                        <Button onClick={cancelRecording} size="icon" variant="ghost" className="text-muted-foreground" data-testid="cancel-recording-btn">
                          <MicOff className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={startRecording} size="icon" variant="outline" disabled={isLoading || isTranscribing || voiceProcessing} className={cn("hover:bg-violet-50 hover:text-violet-600 hover:border-violet-300", voiceMode && "border-violet-300 text-violet-600")} data-testid="voice-input-btn" title="Record voice">
                        {isTranscribing || voiceProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                      </Button>
                    )}
                    
                    <Button onClick={sendMessage} disabled={!inputValue.trim() || isLoading} className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700" data-testid="send-btn">
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                    {voiceMode ? 'Voice mode: speak and messages auto-send. Say "yes/no" to approve actions.' : 'Orka AI can manage tasks, view analytics, and automate. Some actions require confirmation.'}
                  </p>
                </div>
              </Card>
            </TabsContent>
            
            {/* Capabilities Tab — Phase 12: consolidated (Info banner + Orchestrator prompt + unified Capability registry + Custom caps + Learning) */}
            <TabsContent value="capabilities" className="flex-1 mt-0 overflow-y-auto data-[state=inactive]:hidden">
              <div className="space-y-6" data-testid="capabilities-section">

                {/* ========== Phase 12: Info banner ========== */}
                <div
                  className="rounded-xl border bg-gradient-to-br from-violet-50 via-blue-50 to-emerald-50 dark:from-violet-950/30 dark:via-blue-950/30 dark:to-emerald-950/20 p-5 flex gap-4"
                  data-testid="capabilities-info-banner"
                >
                  <div className="w-10 h-10 rounded-lg orka-surface-raised shadow-sm flex items-center justify-center flex-shrink-0">
                    <Brain className="w-5 h-5 text-violet-500" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2">How Orka AI reasons</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Every request flows through the <strong>Orchestrator</strong> — a reasoning layer that (1) restates your intent, (2) picks the right connected knowledge source (Jira, Confluence, Drive, Docs, Sheets, Slides, Calendar, Gmail, Slack, WhatsApp, Orka memory), (3) asks a clarifying question only when it matters, and (4) invokes the right <strong>Capabilities</strong> from the registry below.
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You can tweak the Orchestrator's reasoning prompt, and customise any individual capability's description or add a usage hint to nudge the AI toward (or away from) it.
                    </p>
                  </div>
                </div>

                {/* ========== Phase 12: Orchestrator system prompt (editable) ========== */}
                <OrchestratorPromptCard />

                {/* ========== Unified Capability Registry (built-in + custom + overrides) ========== */}
                {v2Caps.stats && (
                  <Card data-testid="v2-capability-registry-card">
                    <CardHeader className="py-4 px-6 border-b flex flex-row items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Zap className="w-4 h-4 text-violet-500" />
                          Capabilities
                          <Badge variant="secondary" className="ml-2 text-[10px]" data-testid="v2-cap-total">
                            {v2Caps.stats.total} active
                          </Badge>
                          {v2Caps.overrides_count > 0 && (
                            <Badge variant="default" className="text-[10px] bg-blue-500" data-testid="v2-overrides-count">
                              {v2Caps.overrides_count} customised
                            </Badge>
                          )}
                          {v2Caps.stats.invalid_count > 0 && (
                            <Badge variant="destructive" className="text-[10px]" data-testid="v2-cap-invalid">
                              {v2Caps.stats.invalid_count} invalid
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          What Orka AI can do. Click the pencil on any capability to override its description or add a usage hint. Safety levels: L1=auto, L2=affirm, L3=button-confirm, L4=typed-confirm.
                        </CardDescription>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => { setShowCreateCap(true); setGeneratedCap(null); setNewCapDesc(''); }} data-testid="create-capability-btn">
                        <Wand2 className="w-4 h-4 mr-1" />Custom capability
                      </Button>
                    </CardHeader>
                    <CardContent className="p-5 space-y-4">
                      {/* Create-New-Capability dialog (moved here from removed AI Capabilities card) */}
                      {showCreateCap && (
                        <div className="p-4 rounded-lg border bg-muted/20 space-y-3" data-testid="create-capability-dialog">
                          <h4 className="font-medium text-sm flex items-center gap-2"><Wand2 className="w-4 h-4 text-violet-500" />Create New Capability</h4>
                          <p className="text-xs text-muted-foreground">Describe what you want the capability to do. AI will design, code, and deploy it.</p>
                          <Textarea value={newCapDesc} onChange={e => setNewCapDesc(e.target.value)} placeholder="e.g., Summarize my Slack channels daily and create a brief..." rows={2} className="text-sm" data-testid="cap-description-input" />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={generateCapability} disabled={generatingCap || !newCapDesc.trim()} data-testid="generate-cap-btn">
                              {generatingCap ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Generating...</> : <><Wand2 className="w-3 h-3 mr-1" />Generate</>}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setShowCreateCap(false)}>Cancel</Button>
                          </div>
                          {generatedCap && (
                            <div className="mt-3 p-3 bg-card rounded-lg border space-y-2">
                              <h5 className="font-medium text-sm">{generatedCap.name}</h5>
                              <p className="text-xs text-muted-foreground">{generatedCap.description}</p>
                              <div className="flex flex-wrap gap-1">
                                <Badge variant="outline" className="text-[10px]">{generatedCap.risk_tier}</Badge>
                                {(generatedCap.knowledge_sources || []).map((ks, i) => <Badge key={i} variant="secondary" className="text-[10px]">{ks}</Badge>)}
                              </div>
                              {generatedCap.system_prompt && <pre className="text-[10px] bg-muted/50 p-2 rounded max-h-24 overflow-y-auto whitespace-pre-wrap">{generatedCap.system_prompt}</pre>}
                              <Button size="sm" onClick={deployCapability} className="bg-emerald-600 hover:bg-emerald-700" data-testid="deploy-cap-btn">
                                <Play className="w-3 h-3 mr-1" />Deploy Capability
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Safety level summary */}
                      <div className="flex flex-wrap gap-2" data-testid="v2-safety-summary">
                        {Object.entries(v2Caps.stats.by_safety_level || {}).sort().map(([lvl, count]) => {
                          const colorMap = {
                            '1': 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
                            '2': 'bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900',
                            '3': 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
                            '4': 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
                          };
                          const labelMap = { '1': 'Autonomous', '2': 'Affirmation', '3': 'Button', '4': 'Typed' };
                          return (
                            <Badge
                              key={lvl}
                              variant="outline"
                              className={cn('text-[11px] border', colorMap[lvl])}
                              data-testid={`v2-safety-level-${lvl}`}
                            >
                              L{lvl} · {labelMap[lvl]} · {count}
                            </Badge>
                          );
                        })}
                      </div>

                      {/* Domain shortcut bar */}
                      <div className="flex flex-wrap gap-1.5 pb-1" data-testid="v2-domain-shortcuts">
                        <button
                          type="button"
                          onClick={() => setV2DomainFilter('')}
                          className={cn(
                            'text-[11px] px-2 py-1 rounded-full border transition',
                            v2DomainFilter === ''
                              ? 'bg-violet-500 text-white border-violet-500'
                              : 'bg-card hover:bg-accent border-border'
                          )}
                          data-testid="v2-domain-shortcut-all"
                        >
                          All <span className="opacity-60 ml-1">{v2Caps.stats?.total ?? 0}</span>
                        </button>
                        {Object.entries(v2Caps.stats?.by_domain || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([dom, count]) => (
                            <button
                              key={dom}
                              type="button"
                              onClick={() => setV2DomainFilter(dom)}
                              className={cn(
                                'text-[11px] px-2 py-1 rounded-full border transition',
                                v2DomainFilter === dom
                                  ? 'bg-violet-500 text-white border-violet-500'
                                  : 'bg-card hover:bg-accent border-border'
                              )}
                              data-testid={`v2-domain-shortcut-${dom}`}
                            >
                              {v2Caps.domain_labels?.[dom] || dom} <span className="opacity-60 ml-1">{count}</span>
                            </button>
                          ))}
                      </div>

                      {/* Domain groups */}
                      <div className="space-y-3" data-testid="v2-domain-groups">
                        {Object.entries(v2Caps.groups || {})
                          .filter(([domain]) => !v2DomainFilter || domain === v2DomainFilter)
                          .map(([domain, caps]) => (
                          <div key={domain} className="border rounded-lg overflow-hidden" data-testid={`v2-domain-${domain}`}>
                            <div className="px-3 py-2 bg-muted/50 flex items-center justify-between">
                              <span className="text-xs font-semibold uppercase tracking-wide">
                                {v2Caps.domain_labels?.[domain] || domain}
                              </span>
                              <Badge variant="secondary" className="text-[10px]">{caps.length}</Badge>
                            </div>
                            <div className="divide-y">
                              {caps.map((c) => (
                                <div key={c.capability_id}>
                                  <div className="relative group">
                                    <CapabilityTryRow cap={c} />
                                    {/* Override indicator + edit pencil */}
                                    <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      {c.is_customized && (
                                        <Badge variant="default" className="text-[9px] bg-blue-500 h-5" data-testid={`cap-customized-${c.capability_id}`}>
                                          customised
                                        </Badge>
                                      )}
                                      <Button
                                        size="icon" variant="ghost" className="h-7 w-7"
                                        onClick={() => setEditingCapabilityId(
                                          editingCapabilityId === c.capability_id ? null : c.capability_id
                                        )}
                                        title="Customise this capability"
                                        data-testid={`cap-edit-btn-${c.capability_id}`}
                                      >
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    {/* Pinned indicator for customized caps even when not hovered */}
                                    {c.is_customized && (
                                      <div className="absolute top-3 right-3 opacity-100 group-hover:opacity-0 pointer-events-none">
                                        <Badge variant="default" className="text-[9px] bg-blue-500 h-5">
                                          customised
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                  {editingCapabilityId === c.capability_id && (
                                    <CapabilityOverrideEditor
                                      capability={c}
                                      onSaved={refetchV2Caps}
                                      onClose={() => setEditingCapabilityId(null)}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ========== Custom capabilities (user-generated) ========== */}
                {customCapabilities.length > 0 && (
                  <Card data-testid="custom-capabilities-card">
                    <CardHeader className="py-4 px-6 border-b">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Wand2 className="w-5 h-5 text-amber-500" />
                        Your Custom Capabilities
                        <Badge variant="secondary" className="text-[10px]">{customCapabilities.length}</Badge>
                      </CardTitle>
                      <CardDescription>Capabilities you've designed and deployed — edit their descriptions, knowledge sources, or delete them.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {customCapabilities.map(cap => (
                          <div key={cap.capability_id} className="relative">
                            <CapabilityDetailRow capability={cap} isCustom onSave={saveCustomCapability} />
                            <Button size="icon" variant="ghost" className="absolute top-3 right-3 h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={() => deleteCustomCapability(cap.capability_id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ========== PHASE 12 REMOVED: legacy "AI Capabilities" + "System Prompts & AI Functions" cards consolidated into the registry above ========== */}

                {/* Learning Module */}
                <Card>
                  <CardHeader className="py-4 px-6 border-b">
                    <CardTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5" />Learning</CardTitle>
                    <CardDescription>Work through completed tasks. Describe how you did them — Orka learns your process and builds instruction sets for future automation.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {activeLearnTask ? (
                      /* Task walkthrough UI */
                      <div className="p-5 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-sm">{activeLearnTask.title}</h4>
                            <p className="text-xs text-muted-foreground">{activeLearnTask.work_area_id} &middot; {activeLearnTask.task_estimated_effort} effort</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => { setActiveLearnTask(null); setCleanedResult(null); setLearnDescription(''); }}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium">Describe how you completed this task (voice or type):</label>
                          <div className="relative">
                            <Textarea value={learnDescription} onChange={e => setLearnDescription(e.target.value)} rows={4} placeholder="Walk through your process step by step... e.g., First I opened the spreadsheet, then I filtered by date range..." className="text-sm pr-12" data-testid="learn-description" />
                            <div className="absolute bottom-2 right-2 flex gap-1">
                              {isLearnRecording ? (
                                <>
                                  <span className="text-[10px] text-red-500 animate-pulse mr-1">{learnDuration}s</span>
                                  <Button size="icon" variant="ghost" className="h-7 w-7 bg-red-100 text-red-600" onClick={stopLearnRecording}><Square className="w-3 h-3 fill-current" /></Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelLearnRecording}><MicOff className="w-3 h-3" /></Button>
                                </>
                              ) : (
                                <Button size="icon" variant="ghost" className="h-7 w-7 hover:bg-violet-100 hover:text-violet-600" onClick={startLearnRecording} disabled={isLearnTranscribing} title="Record voice walkthrough" data-testid="learn-voice-btn">
                                  {isLearnTranscribing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button size="sm" onClick={cleanUpDescription} disabled={cleaningUp || !learnDescription.trim()} data-testid="cleanup-btn">
                            {cleaningUp ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Cleaning up...</> : <><Wand2 className="w-3 h-3 mr-1" />Clean Up with AI</>}
                          </Button>
                        </div>

                        {cleanedResult && (
                          <div className="border rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-3">
                            <h5 className="font-medium text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />AI-Cleaned Instructions</h5>
                            <p className="text-sm">{cleanedResult.cleaned_instructions}</p>
                            {cleanedResult.steps?.length > 0 && (
                              <div className="space-y-1">
                                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Steps</label>
                                <ol className="list-decimal list-inside text-sm space-y-1">
                                  {cleanedResult.steps.map((step, i) => <li key={i} className="text-sm">{step}</li>)}
                                </ol>
                              </div>
                            )}
                            {cleanedResult.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1">{cleanedResult.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
                            )}
                            {cleanedResult.tips && <p className="text-xs text-muted-foreground italic">{cleanedResult.tips}</p>}
                            <Button size="sm" onClick={saveLearning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-learning-btn">
                              <Save className="w-3 h-3 mr-1" />Save to Learning Library
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="divide-y">
                        {/* Filter and task list */}
                        <div className="p-4 flex items-center gap-2">
                          <Search className="w-4 h-4 text-muted-foreground" />
                          <Input value={learningFilter} onChange={e => setLearningFilter(e.target.value)} placeholder="Search completed tasks..." className="h-8 text-sm flex-1" data-testid="learning-search" onKeyDown={e => { if (e.key === 'Enter') fetchLearningTasks(0); }} />
                          <Button size="sm" variant="outline" onClick={() => fetchLearningTasks(0)} className="h-8"><Filter className="w-3 h-3 mr-1" />Filter</Button>
                          <Badge variant="secondary" className="text-xs">{learningTotal} tasks</Badge>
                        </div>
                        
                        {learningTasks.length === 0 ? (
                          <div className="p-8 text-center">
                            <GraduationCap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                            <p className="text-sm text-muted-foreground">No completed tasks to learn from yet. Complete tasks and come back to teach Orka your process.</p>
                          </div>
                        ) : (
                          <>
                            {learningTasks.map(task => (
                              <button key={task.task_id} className="w-full text-left px-5 py-3.5 hover:bg-muted/30 transition-colors flex items-center gap-3" onClick={() => startLearnTask(task)} data-testid={`learn-task-${task.task_id}`}>
                                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                                  <GraduationCap className="w-4 h-4 text-violet-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{task.title}</div>
                                  <div className="text-xs text-muted-foreground">{task.work_area_id || 'General'} &middot; Effort: {task.task_estimated_effort || 1}</div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </button>
                            ))}
                            {/* Pagination */}
                            {learningTotal > 10 && (
                              <div className="p-3 flex justify-center gap-2">
                                <Button size="sm" variant="outline" disabled={learningPage === 0} onClick={() => fetchLearningTasks(learningPage - 1)}>Previous</Button>
                                <span className="text-xs text-muted-foreground self-center">Page {learningPage + 1} of {Math.ceil(learningTotal / 10)}</span>
                                <Button size="sm" variant="outline" disabled={(learningPage + 1) * 10 >= learningTotal} onClick={() => fetchLearningTasks(learningPage + 1)}>Next</Button>
                              </div>
                            )}
                          </>
                        )}

                        {/* Learning Library */}
                        {learningInstructions.length > 0 && (
                          <div>
                            <div className="px-5 py-2 bg-emerald-50 dark:bg-emerald-950/20">
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Learning Library ({learningInstructions.length} instructions)</span>
                            </div>
                            {learningInstructions.map(inst => (
                              <div key={inst.instruction_id} className="px-5 py-3 border-t hover:bg-muted/20 group">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{inst.task_title}</div>
                                    <p className="text-xs text-muted-foreground mt-0.5">{inst.cleaned_instructions}</p>
                                    {inst.tags?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">{inst.tags.map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
                                    )}
                                  </div>
                                  <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive" onClick={() => deleteLearning(inst.instruction_id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Recent Actions Tab (Phase 2.5) */}
            <TabsContent value="actions" className="flex-1 mt-0 overflow-y-auto data-[state=inactive]:hidden" data-testid="actions-tab-content">
              <RecentActionsPanel />
            </TabsContent>

            {/* Knowledge Sources Tab (Phase 14) */}
            <TabsContent value="knowledge" className="flex-1 mt-0 overflow-y-auto data-[state=inactive]:hidden px-2" data-testid="knowledge-tab-content">
              <KnowledgeSourcesPanel />
            </TabsContent>

            {/* Reasoning Preview Tab (Phase 13) */}
            <TabsContent value="reasoning" className="flex-1 mt-0 overflow-y-auto data-[state=inactive]:hidden px-2" data-testid="reasoning-tab-content">
              <ReasoningPreviewPanel />
            </TabsContent>
            
            {/* Workflows Tab */}
            <TabsContent value="workflows" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <Card className="h-full overflow-hidden">
                <CardHeader className="py-4 px-6 border-b flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><GitBranch className="w-5 h-5" />Workflows</CardTitle>
                    <CardDescription>Visual workflow builder for automated operations</CardDescription>
                  </div>
                  <Button onClick={createWorkflow} size="sm"><Plus className="w-4 h-4 mr-1" />New Workflow</Button>
                </CardHeader>
                <ScrollArea className="h-[calc(100%-5rem)] p-4">
                  {workflows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <GitBranch className="w-12 h-12 text-muted-foreground mb-4" />
                      <h3 className="font-medium mb-1">No workflows yet</h3>
                      <p className="text-sm text-muted-foreground max-w-sm mb-4">Create automated workflows to chain AI operations together.</p>
                      <Button onClick={createWorkflow}><Plus className="w-4 h-4 mr-1" />Create Workflow</Button>
                    </div>
                  ) : (
                    <div className="grid gap-4">{workflows.map((wf) => <WorkflowCard key={wf.workflow_id} workflow={wf} onEdit={() => editWorkflow(wf)} onDelete={deleteWorkflow} onRun={runWorkflow} />)}</div>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>
            
            {/* Scheduled Jobs Tab */}
            <TabsContent value="jobs" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <Card className="h-full overflow-hidden">
                <CardHeader className="py-4 px-6 border-b flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><Timer className="w-5 h-5" />Scheduled Jobs</CardTitle>
                    <CardDescription>Automated briefings, reports, and recurring AI tasks</CardDescription>
                  </div>
                  <Button onClick={createScheduledJob} size="sm"><Plus className="w-4 h-4 mr-1" />New Job</Button>
                </CardHeader>
                <ScrollArea className="h-[calc(100%-5rem)] p-4">
                  <BlockedJobsBanner />
                  {scheduledJobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Timer className="w-12 h-12 text-muted-foreground mb-4" />
                      <h3 className="font-medium mb-1">No scheduled jobs</h3>
                      <p className="text-sm text-muted-foreground max-w-sm mb-4">Set up recurring AI tasks like morning briefings or EOD reports.</p>
                      <Button onClick={createScheduledJob}><Plus className="w-4 h-4 mr-1" />Create Job</Button>
                    </div>
                  ) : (
                    <div className="grid gap-4">{scheduledJobs.map((job) => (
                      <Card key={job.job_id} className={cn("hover:shadow-md orka-transition-colors", !job.is_active && "opacity-60")}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Timer className="w-4 h-4 text-violet-500" />
                                <span className="font-medium">{job.name}</span>
                                <Badge variant={job.is_active ? "default" : "secondary"} className="text-xs">{job.is_active ? 'Active' : 'Paused'}</Badge>
                                {job.is_blocked && <Badge variant="destructive" className="text-xs">Blocked</Badge>}
                                {(job.consecutive_failures || 0) > 0 && !job.is_blocked && (
                                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/30">
                                    {job.consecutive_failures} fail{job.consecutive_failures === 1 ? '' : 's'}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">Type: {job.job_type}</p>
                              <div className="text-xs text-muted-foreground mt-1">
                                {job.schedule?.type === 'daily' && `Daily at ${job.schedule.time}`}
                                {job.last_run_at && ` • Last: ${new Date(job.last_run_at).toLocaleDateString()}`}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => runJobNow(job.job_id)} title="Run Now">
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleJob(job)}>
                                {job.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteJob(job)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}</div>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>

            {/* Memory Tab */}
            <TabsContent value="memory" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <div className="grid grid-rows-[auto_auto] gap-4 h-full overflow-y-auto pb-4">
                {/* Phase 6 — Operational Memory v2 (pinned/learned/editable rules) */}
                <div className="min-h-[480px]">
                  <MemoryV2Panel />
                </div>

                {/* Document RAG (legacy, embeddings) */}
                <div className="grid grid-cols-2 gap-4">
                {/* Search Panel */}
                <Card className="overflow-hidden">
                  <CardHeader className="py-4 px-6 border-b">
                    <CardTitle className="flex items-center gap-2"><Search className="w-5 h-5" />Search Documents</CardTitle>
                    <CardDescription>Find relevant context from stored documents (RAG)</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="flex gap-2 mb-4">
                      <Input
                        value={memoryQuery}
                        onChange={(e) => setMemoryQuery(e.target.value)}
                        placeholder="Search your documents..."
                        onKeyPress={(e) => e.key === 'Enter' && searchMemory()}
                      />
                      <Button onClick={searchMemory}><Search className="w-4 h-4" /></Button>
                    </div>
                    <ScrollArea className="h-64">
                      {memoryResults.length > 0 ? (
                        <div className="space-y-3">
                          {memoryResults.map((result, i) => (
                            <div key={i} className="p-3 rounded-lg bg-muted/50">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">{(result.score * 100).toFixed(0)}% match</Badge>
                                <span className="text-xs text-muted-foreground">{result.memory_type}</span>
                              </div>
                              <p className="text-sm line-clamp-3">{result.content}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">Search results will appear here</p>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Upload Panel */}
                <Card className="overflow-hidden">
                  <CardHeader className="py-4 px-6 border-b">
                    <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" />Store Document</CardTitle>
                    <CardDescription>Add documents for AI to reference (RAG)</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3 mb-4">
                      <Input
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="Document title"
                      />
                      <Textarea
                        value={uploadContent}
                        onChange={(e) => setUploadContent(e.target.value)}
                        placeholder="Paste document content here..."
                        className="min-h-32"
                      />
                      <Button onClick={uploadDocument} className="w-full">
                        <Upload className="w-4 h-4 mr-2" />Store Document
                      </Button>
                    </div>
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-2">Stored Documents ({documents.length})</h4>
                      <ScrollArea className="h-32">
                        {documents.map((doc) => (
                          <div key={doc.doc_id} className="flex items-center justify-between py-2 px-2 hover:bg-muted rounded">
                            <div>
                              <div className="text-sm font-medium">{doc.title}</div>
                              <div className="text-xs text-muted-foreground">{doc.doc_type}</div>
                            </div>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteDocument(doc.doc_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  </CardContent>
                </Card>
                </div>
              </div>
            </TabsContent>

            {/* Personalization Tab */}
            <TabsContent value="routing" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <ModelRoutingPanel />
            </TabsContent>


            <TabsContent value="personalization" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <Card className="h-full overflow-hidden">
                <CardHeader className="py-4 px-6 border-b">
                  <CardTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5" />AI Personalization</CardTitle>
                  <CardDescription>Customize how Orka AI communicates and operates</CardDescription>
                </CardHeader>
                <ScrollArea className="h-[calc(100%-5rem)] p-4">
                  {personalization ? (
                    <div className="space-y-6 max-w-2xl">
                      {/* Tone Setting */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Communication Tone</label>
                        <Select value={personalization.tone} onValueChange={(v) => updatePersonalization({ tone: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="casual">Casual</SelectItem>
                            <SelectItem value="direct">Direct</SelectItem>
                            <SelectItem value="friendly">Friendly</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">How Orka AI speaks to you</p>
                      </div>

                      {/* Verbosity Setting */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Response Detail Level</label>
                        <Select value={personalization.verbosity} onValueChange={(v) => updatePersonalization({ verbosity: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="concise">Concise</SelectItem>
                            <SelectItem value="balanced">Balanced</SelectItem>
                            <SelectItem value="detailed">Detailed</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">How much detail in responses</p>
                      </div>

                      {/* Formatting Setting */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Response Format</label>
                        <Select value={personalization.formatting} onValueChange={(v) => updatePersonalization({ formatting: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="structured">Structured (bullets, headers)</SelectItem>
                            <SelectItem value="narrative">Narrative (prose)</SelectItem>
                            <SelectItem value="minimal">Minimal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* AI Proactivity */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">AI Proactivity</label>
                        <Select value={personalization.ai_proactivity} onValueChange={(v) => updatePersonalization({ ai_proactivity: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minimal">Minimal (only when asked)</SelectItem>
                            <SelectItem value="moderate">Moderate (helpful suggestions)</SelectItem>
                            <SelectItem value="proactive">Proactive (anticipate needs)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Working Hours */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Working Hours</label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="time"
                            value={personalization.working_hours?.start || '09:00'}
                            onChange={(e) => updatePersonalization({ working_hours: { ...personalization.working_hours, start: e.target.value } })}
                            className="w-32"
                          />
                          <span className="text-muted-foreground">to</span>
                          <Input
                            type="time"
                            value={personalization.working_hours?.end || '18:00'}
                            onChange={(e) => updatePersonalization({ working_hours: { ...personalization.working_hours, end: e.target.value } })}
                            className="w-32"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Orka won't schedule tasks outside these hours</p>
                      </div>

                      {/* Do Rules */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-emerald-600">✓ Always Do</label>
                        <div className="flex gap-2">
                          <Input
                            value={newDoRule}
                            onChange={(e) => setNewDoRule(e.target.value)}
                            placeholder="Add a 'do' rule..."
                            onKeyPress={(e) => e.key === 'Enter' && addRule('do', newDoRule)}
                          />
                          <Button onClick={() => addRule('do', newDoRule)} variant="outline" size="icon"><Plus className="w-4 h-4" /></Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {personalization.do_rules?.map((rule, i) => (
                            <Badge key={i} variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              {rule}
                              <button onClick={() => removeRule('do', rule)} className="ml-1 hover:text-red-500">×</button>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Don't Rules */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-red-600">✗ Never Do</label>
                        <div className="flex gap-2">
                          <Input
                            value={newDontRule}
                            onChange={(e) => setNewDontRule(e.target.value)}
                            placeholder="Add a 'don't' rule..."
                            onKeyPress={(e) => e.key === 'Enter' && addRule('dont', newDontRule)}
                          />
                          <Button onClick={() => addRule('dont', newDontRule)} variant="outline" size="icon"><Plus className="w-4 h-4" /></Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {personalization.dont_rules?.map((rule, i) => (
                            <Badge key={i} variant="secondary" className="bg-red-50 text-red-700 border-red-200">
                              {rule}
                              <button onClick={() => removeRule('dont', rule)} className="ml-1 hover:text-red-500">×</button>
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Custom Instructions */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Custom Instructions</label>
                        <Textarea
                          value={personalization.custom_instructions || ''}
                          onChange={(e) => updatePersonalization({ custom_instructions: e.target.value })}
                          placeholder="Any additional instructions for how Orka AI should behave..."
                          rows={4}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>

            {/* Observability Tab */}
            <TabsContent value="observability" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <div className="grid grid-cols-3 gap-4 h-full">
                {/* Usage Summary */}
                <Card className="col-span-2">
                  <CardHeader className="py-4 px-6 border-b">
                    <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5" />AI Usage (7 Days)</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {usageSummary ? (
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-violet-600">{usageSummary.total_calls || 0}</div>
                          <div className="text-xs text-muted-foreground">Total Calls</div>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-emerald-600">{usageSummary.successful_calls || 0}</div>
                          <div className="text-xs text-muted-foreground">Successful</div>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-600">{(usageSummary.total_tokens || 0).toLocaleString()}</div>
                          <div className="text-xs text-muted-foreground">Tokens Used</div>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-amber-600">${(usageSummary.estimated_cost_usd || 0).toFixed(4)}</div>
                          <div className="text-xs text-muted-foreground">Est. Cost</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    
                    {/* Daily Usage Chart (simplified) */}
                    <div>
                      <h4 className="text-sm font-medium mb-3">Daily Usage (30 Days)</h4>
                      <div className="h-32 flex items-end gap-1">
                        {dailyUsage.slice(-30).map((day, i) => {
                          const maxCalls = Math.max(...dailyUsage.map(d => d.calls || 1), 1);
                          const height = ((day.calls || 0) / maxCalls) * 100;
                          return (
                            <div key={i} className="flex-1 bg-violet-200 hover:bg-violet-400 transition-colors rounded-t" style={{ height: `${Math.max(height, 4)}%` }}
                              title={`${day.date}: ${day.calls} calls, ${day.tokens} tokens`} />
                          );
                        })}
                      </div>
                      {dailyUsage.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No usage data yet</p>
                      )}
                    </div>
                    
                    {/* Model Breakdown */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium mb-3">Model Breakdown</h4>
                      <div className="space-y-2">
                        {modelBreakdown.map((model, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span className="font-mono text-sm">{model.model}</span>
                            <div className="flex gap-4 text-sm text-muted-foreground">
                              <span>{model.calls} calls</span>
                              <span>{model.tokens?.toLocaleString()} tokens</span>
                              <span className="text-amber-600">${model.cost?.toFixed(4)}</span>
                            </div>
                          </div>
                        ))}
                        {modelBreakdown.length === 0 && <p className="text-sm text-muted-foreground">No model usage data</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Errors */}
                <Card>
                  <CardHeader className="py-4 px-6 border-b">
                    <CardTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-red-500" />Recent Errors</CardTitle>
                  </CardHeader>
                  <ScrollArea className="h-[calc(100%-4rem)]">
                    <CardContent className="p-4">
                      {recentErrors.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500 opacity-50" />
                          <p>No recent errors!</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {recentErrors.map((error, i) => (
                            <div key={i} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="text-xs text-red-600 font-medium">{error.call_type}</div>
                              <div className="text-sm mt-1 line-clamp-2">{error.error}</div>
                              <div className="text-xs text-muted-foreground mt-1">{new Date(error.timestamp).toLocaleString()}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </ScrollArea>
                </Card>
              </div>
            </TabsContent>

            {/* Multi-Orca Tab */}
            <TabsContent value="orca" className="flex-1 mt-0 data-[state=inactive]:hidden">
              <Card className="h-full overflow-hidden">
                <CardHeader className="py-4 px-6 border-b">
                  <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" />Multi-Orca Communication</CardTitle>
                  <CardDescription>Messages between Orca AI instances across users</CardDescription>
                </CardHeader>
                <div className="p-4">
                  <Tabs defaultValue="inbox">
                    <TabsList>
                      <TabsTrigger value="inbox">Inbox ({orcaInbox.length})</TabsTrigger>
                      <TabsTrigger value="pending">Pending Approval ({orcaPending.length})</TabsTrigger>
                      <TabsTrigger value="sent">Sent ({orcaSent.length})</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="inbox">
                      <ScrollArea className="h-64">
                        {orcaInbox.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No messages in your inbox</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {orcaInbox.map((msg) => (
                              <Card key={msg.message_id}>
                                <CardContent className="p-3">
                                  <div className="flex items-start gap-2">
                                    <Bot className="w-5 h-5 text-violet-500 mt-0.5" />
                                    <div className="flex-1">
                                      <div className="text-xs text-muted-foreground">From: {msg.from_orca_id}</div>
                                      <p className="text-sm mt-1">{msg.message}</p>
                                      <div className="text-xs text-muted-foreground mt-1">{new Date(msg.created_at).toLocaleString()}</div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </TabsContent>
                    
                    <TabsContent value="pending">
                      <ScrollArea className="h-64">
                        {orcaPending.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No messages pending approval</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {orcaPending.map((msg) => (
                              <Card key={msg.message_id} className="border-amber-200 bg-amber-50/50">
                                <CardContent className="p-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <Badge variant="outline" className="text-xs mb-1">Cross-Org Message</Badge>
                                      <div className="text-xs text-muted-foreground">From: {msg.from_orca_id}</div>
                                      <p className="text-sm mt-1">{msg.message}</p>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" onClick={() => rejectOrcaMessage(msg.message_id)}>Reject</Button>
                                      <Button size="sm" onClick={() => approveOrcaMessage(msg.message_id)}>Approve</Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </TabsContent>
                    
                    <TabsContent value="sent">
                      <ScrollArea className="h-64">
                        {orcaSent.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Send className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No sent messages</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {orcaSent.map((msg) => (
                              <Card key={msg.message_id}>
                                <CardContent className="p-3">
                                  <div className="flex items-start gap-2">
                                    <Send className="w-5 h-5 text-blue-500 mt-0.5" />
                                    <div className="flex-1">
                                      <div className="text-xs text-muted-foreground">To: {msg.to_orca_id}</div>
                                      <p className="text-sm mt-1">{msg.message}</p>
                                      <Badge variant="outline" className="text-xs mt-1">{msg.status}</Badge>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </div>
              </Card>
            </TabsContent>
            
            {/* Reply Library Tab */}
            <TabsContent value="reply-library" className="flex-1 mt-0 overflow-y-auto data-[state=inactive]:hidden">
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-violet-500" />Reply Library</CardTitle>
                    <CardDescription>Save and manage reply templates. AI uses these for tone-matching when drafting replies in Switchboard.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Add new reply */}
                    <div className="border rounded-lg p-3 space-y-2 bg-muted/20">
                      <Input placeholder="Context (e.g., 'Client asking for deadline extension')" value={newReply.context} onChange={e => setNewReply(r => ({...r, context: e.target.value}))} data-testid="reply-library-context" />
                      <Textarea placeholder="Reply text (the actual reply template)" value={newReply.reply_text} onChange={e => setNewReply(r => ({...r, reply_text: e.target.value}))} className="min-h-[80px]" data-testid="reply-library-text" />
                      <div className="flex gap-2">
                        <Input placeholder="Tags (comma-separated)" value={newReply.tags} onChange={e => setNewReply(r => ({...r, tags: e.target.value}))} className="flex-1" />
                        <select value={newReply.channel} onChange={e => setNewReply(r => ({...r, channel: e.target.value}))} className="border rounded-md px-3 text-sm bg-background">
                          <option value="">Any channel</option>
                          <option value="email">Email</option>
                          <option value="slack">Slack</option>
                          <option value="whatsapp">WhatsApp</option>
                        </select>
                        <Button onClick={addReply} size="sm" data-testid="add-reply-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
                      </div>
                    </div>
                    
                    {/* Reply list */}
                    {replyLibrary.length === 0 ? (
                      <div className="text-center py-8">
                        <BookOpen className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-sm text-muted-foreground">No replies yet. Add your first template above, or AI will auto-learn as you send replies from Switchboard.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {replyLibrary.map(reply => (
                          <div key={reply.reply_id} className="border rounded-lg p-3 hover:bg-accent/30 transition-colors" data-testid={`reply-item-${reply.reply_id}`}>
                            {editingReply === reply.reply_id ? (
                              <div className="space-y-2">
                                <Input defaultValue={reply.context} onChange={e => { reply.context = e.target.value; }} />
                                <Textarea defaultValue={reply.reply_text} onChange={e => { reply.reply_text = e.target.value; }} className="min-h-[60px]" />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={() => updateReply(reply)}><CheckCircle2 className="w-3 h-3 mr-1" />Save</Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingReply(null)}>Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium">{reply.context}</p>
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{reply.reply_text}</p>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingReply(reply.reply_id)} data-testid={`edit-reply-${reply.reply_id}`}><Edit2 className="w-3 h-3" /></Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteReply(reply.reply_id)} data-testid={`delete-reply-${reply.reply_id}`}><Trash2 className="w-3 h-3" /></Button>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  {reply.tags?.map(tag => (
                                    <Badge key={tag} variant="outline" className="text-[10px]"><Tag className="w-2.5 h-2.5 mr-0.5" />{tag}</Badge>
                                  ))}
                                  {reply.channel && <Badge variant="secondary" className="text-[10px]">{reply.channel}</Badge>}
                                  <span className="text-[10px] text-muted-foreground ml-auto">Used {reply.usage_count || 0}x</span>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Workflow Editor Modal */}
      {showWorkflowEditor && (
        <WorkflowEditor
          workflow={editingWorkflow}
          onSave={saveWorkflow}
          onClose={() => { setShowWorkflowEditor(false); setEditingWorkflow(null); }}
          capabilities={capabilities}
        />
      )}
    </PageLayout>
  );
};

export default AIPage;
